/**
 * DataCollectionIndicator Component - Indicador de Dados Coletados
 * @module features/chat/components/DataCollectionIndicator
 *
 * Componente que exibe o progresso da coleta de dados do usuario.
 * Mostra checkmarks para campos preenchidos e indicadores vazios para pendentes.
 *
 * T006-FE: Indicador de Dados Coletados
 *
 * Features:
 * - Exibe status de: Nome, Email, Empresa, Cargo, Consentimento
 * - Checkmarks visuais para campos preenchidos
 * - Integracao com sessionStore.userData
 * - Acessibilidade WCAG AA (aria-checked, semantic list)
 * - Estilo compacto para sidebar ou fixed position
 */

import { cn } from '@/lib/utils';
import { Check, Circle } from 'lucide-react';
import { getUserData, useSessionStore } from '../stores/sessionStore';

// =============================================================================
// TYPES
// =============================================================================

export interface DataCollectionIndicatorProps {
  /**
   * Classes CSS adicionais para posicionamento
   * @example "fixed right-4 top-4" ou "mt-4"
   */
  className?: string;
}

interface FieldIndicator {
  key: string;
  label: string;
  isFilled: boolean;
}

// =============================================================================
// HELPERS
// =============================================================================

/**
 * Verifica se um valor de string e considerado "preenchido"
 * Strings vazias ou apenas whitespace sao consideradas nao preenchidas
 */
function isFieldFilled(value: string | undefined): boolean {
  if (value === undefined) return false;
  return value.trim() !== '';
}

// =============================================================================
// COMPONENT
// =============================================================================

/**
 * Indicador visual do progresso de coleta de dados
 *
 * Exibe uma lista de campos com indicadores visuais mostrando
 * quais dados ja foram coletados durante o assessment.
 *
 * @example
 * ```tsx
 * // Em um sidebar
 * <aside className="w-64">
 *   <DataCollectionIndicator />
 * </aside>
 *
 * // Fixed no canto
 * <DataCollectionIndicator className="fixed right-4 top-20" />
 * ```
 */
export function DataCollectionIndicator({ className }: DataCollectionIndicatorProps) {
  const userData = useSessionStore(getUserData);

  // Monta lista de campos com status de preenchimento
  const fields: FieldIndicator[] = [
    {
      key: 'name',
      label: 'Nome',
      isFilled: isFieldFilled(userData.name),
    },
    {
      key: 'email',
      label: 'Email',
      isFilled: isFieldFilled(userData.email),
    },
    {
      key: 'company',
      label: 'Empresa',
      isFilled: isFieldFilled(userData.company),
    },
    {
      key: 'role',
      label: 'Cargo',
      isFilled: isFieldFilled(userData.role),
    },
    {
      key: 'consent',
      label: 'Consentimento',
      isFilled: userData.consentGiven === true,
    },
  ];

  // Calcula progresso
  const filledCount = fields.filter((f) => f.isFilled).length;
  const totalCount = fields.length;

  return (
    <section
      aria-label="Progresso de dados coletados"
      className={cn('rounded-lg border border-border bg-card p-4 shadow-sm', className)}
    >
      <header className="mb-3">
        <h3 className="text-sm font-medium text-foreground">Dados Coletados</h3>
        <p className="text-xs text-muted-foreground">
          {filledCount} de {totalCount} campos
        </p>
      </header>

      <ul className="space-y-2">
        {fields.map((field) => (
          <li
            key={field.key}
            aria-checked={field.isFilled}
            className={cn(
              'flex items-center gap-2 text-sm transition-colors',
              field.isFilled ? 'text-foreground' : 'text-muted-foreground'
            )}
          >
            {field.isFilled ? (
              <Check
                className="h-4 w-4 text-green-500 flex-shrink-0"
                aria-hidden="true"
                data-checked="true"
              />
            ) : (
              <Circle
                className="h-4 w-4 text-muted-foreground/50 flex-shrink-0"
                aria-hidden="true"
                data-checked="false"
              />
            )}
            <span>{field.label}</span>
          </li>
        ))}
      </ul>

      {/* Barra de progresso visual - informacao ja disponivel no header */}
      <div className="mt-3 pt-3 border-t border-border" aria-hidden="true">
        <div className="h-1.5 w-full bg-muted rounded-full overflow-hidden">
          <div
            className="h-full bg-green-500 transition-all duration-300 ease-out"
            style={{ width: `${(filledCount / totalCount) * 100}%` }}
          />
        </div>
      </div>
    </section>
  );
}

export default DataCollectionIndicator;
